import { getDeckStats, type DeckOrder } from "./deck.js";

export type CardsPerPage = 1 | 2 | 4;
export type PrintSizeMode = "original" | "large";

export interface PrintSlot {
  /** Zero-based card index in the complete, canonical deck. */
  cardIndex: number;
  xMm: number;
  yMm: number;
}

export interface PrintPlan {
  cardCount: number;
  pageCount: number;
  diameterMm: number;
  pageWidthMm: number;
  pageHeightMm: number;
  dpi: number;
  sizeMode: PrintSizeMode;
  pages: PrintSlot[][];
}

/**
 * Back of the same A4 sheet, flipped on its long edge. Only positions mirror;
 * the artwork itself stays readable. Partial sheets keep the matching holes.
 */
export function getDuplexSlots(plan: PrintPlan, pageIndex: number): PrintSlot[] {
  if (!Number.isInteger(pageIndex) || pageIndex < 0 || pageIndex >= plan.pages.length) {
    throw new RangeError("Cette page n’existe pas dans le jeu à imprimer.");
  }
  return plan.pages[pageIndex].map((slot) => ({
    ...slot,
    xMm: plan.pageWidthMm - slot.xMm - plan.diameterMm,
  }));
}

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_MM = 12;
const FOOTER_MM = 16;
const GAP_MM = 12;
const DPI = 300;

/** A4 portrait. Density changes pagination, while original cards stay 85 mm. */
export function getPrintPlan(
  order: DeckOrder,
  cardsPerPage: CardsPerPage,
  sizeMode: PrintSizeMode = "original",
): PrintPlan {
  if (![1, 2, 4].includes(cardsPerPage)) {
    throw new RangeError("Choisissez 1, 2 ou 4 cartes par page.");
  }
  if (sizeMode !== "original" && sizeMode !== "large") {
    throw new RangeError("Choisissez le format original ou agrandi.");
  }
  const cardCount = getDeckStats(order).cards;
  const diameterMm =
    sizeMode === "original"
      ? 85
      : cardsPerPage === 1
        ? 180
        : cardsPerPage === 2
          ? 125
          : 87;
  const columns = cardsPerPage === 4 ? 2 : 1;
  const rows = cardsPerPage === 1 ? 1 : 2;
  const gridWidth = columns * diameterMm + (columns - 1) * GAP_MM;
  const gridHeight = rows * diameterMm + (rows - 1) * GAP_MM;
  const startX = (PAGE_WIDTH_MM - gridWidth) / 2;
  const startY =
    MARGIN_MM + (PAGE_HEIGHT_MM - MARGIN_MM - FOOTER_MM - gridHeight) / 2;
  const pageCount = Math.ceil(cardCount / cardsPerPage);
  const pages = Array.from({ length: pageCount }, (_, page) =>
    Array.from(
      { length: Math.min(cardsPerPage, cardCount - page * cardsPerPage) },
      (_, slot) => ({
        cardIndex: page * cardsPerPage + slot,
        xMm: startX + (slot % columns) * (diameterMm + GAP_MM),
        yMm: startY + Math.floor(slot / columns) * (diameterMm + GAP_MM),
      }),
    ),
  );
  return {
    cardCount,
    pageCount,
    diameterMm,
    pageWidthMm: PAGE_WIDTH_MM,
    pageHeightMm: PAGE_HEIGHT_MM,
    dpi: DPI,
    sizeMode,
    pages,
  };
}
